import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import {
  getWhatsAppGroupInviteSettings,
  setWhatsAppGroupInviteSettings,
} from "../../../../database/community/whatsappGroupInvite";

type Props = {
  communityId: string;
  disabled?: boolean;
};

const callableErrorMessage = (error: any): string =>
  String(error?.message || "Could not save the WhatsApp group invite.")
    .replace(/^.*?:\s*/, "")
    .trim();

export default function WhatsAppGroupInviteSettings({
  communityId,
  disabled = false,
}: Props) {
  const [groupInviteUrl, setGroupInviteUrl] = useState("");
  const [loading, setLoading] = useState(Boolean(communityId));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setMessage("");
    setError("");
    if (!communityId) {
      setGroupInviteUrl("");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    getWhatsAppGroupInviteSettings(communityId)
      .then((settings) => {
        if (active) setGroupInviteUrl(settings.groupInviteUrl || "");
      })
      .catch((loadError) => {
        if (active) setError(callableErrorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [communityId]);

  const save = async () => {
    if (!communityId || saving) return;
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const result = await setWhatsAppGroupInviteSettings({
        communityId,
        groupInviteUrl,
      });
      setGroupInviteUrl(result.groupInviteUrl || "");
      setMessage(
        result.groupInviteUrl
          ? "Saved. New onboarding welcome messages will include this invite."
          : "Removed. New onboarding welcome messages will not include a group invite."
      );
    } catch (saveError) {
      setError(callableErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  if (!communityId) {
    return (
      <div style={{ fontSize: 13, color: "#666", marginTop: 12 }}>
        Create the community before adding its WhatsApp group invite.
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 16,
        padding: 16,
        border: "1px solid #e1e1e1",
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
        WhatsApp community group invite
      </div>
      <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
        Optional and stored server-side. When set, the existing onboarding
        welcome message includes this link and tells members that joining may
        reveal their WhatsApp profile and phone number to the group.
      </div>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label="WhatsApp group invite URL"
        placeholder="https://chat.whatsapp.com/..."
        value={groupInviteUrl}
        disabled={disabled || loading || saving}
        onChange={(event) => setGroupInviteUrl(event.target.value)}
        error={Boolean(error)}
        helperText={error || message || "Leave empty to disable the invite."}
        inputProps={{ autoCapitalize: "none", autoCorrect: "off" }}
      />
      {!disabled ? (
        <Button
          type="button"
          variant="outlined"
          color="primary"
          disabled={loading || saving}
          onClick={save}
          style={{ marginTop: 12 }}
        >
          {saving ? "Saving…" : "Save group invite"}
        </Button>
      ) : null}
    </div>
  );
}
